if (!customElements.get('slideshow-component')) {
  customElements.define(
    'slideshow-component',
    class SlideshowComponent extends HTMLElement {
      constructor() {
        super();

        this.sliderControls = this.querySelector('.swiper-controls');
        this.sliderInstance = false;
        this.enableSlider = this.dataset.enableSlider === 'true';
        this.selectedIndex = this.selectedIndex;
        this.sectionId = this.dataset.sectionId;
        this.section = this.closest(`.section-${this.sectionId}`);
        this.containerWidth = this.dataset.container;
        this.cleanupScrollAnimations = [];
        this.enableZoom = this.dataset.enableZoom ? this.dataset.enableZoom === 'true' : true;
        this.borderRadius = this.dataset.borderRadius;
        this.zoom = 0;
        this.initialAutoplayDelayActive = false;
        this.initialAutoplayDelayTimer = null;
        this.initialAutoplayLoadHandler = null;
        this.initialAutoplayMediaWaitTimer = null;
        this.autoplayStartOnInteraction = this.sectionId?.includes('slideshow_CqxCR4_sync') || false;
        this.autoplayInteractionHandler = null;
        this.autoplayInteractionEvents = ['pointerdown', 'touchstart', 'wheel', 'keydown'];
        this.paginationPointerIndex = null;
        this.autoplayHoverPauseReady = false;
        this.deferredSlidePreloadScheduled = false;

        this.setupSlideImageLinks();

        if (!this.enableSlider) return;

        this.setupZoom();
        window.addEventListener('resize', this.setupZoom.bind(this));

        this.initSlider();
      }

      static get observedAttributes() {
        return ['selected-index'];
      }

      get selectedIndex() {
        return parseInt(this.getAttribute('selected-index')) || 0;
      }

      set selectedIndex(index) {
        this.setAttribute('selected-index', `${index}`);
      }

      setupSlideImageLinks() {
        const dragThreshold = 8;
        const resetPreventClick = () => {
          window.clearTimeout(this.slideImageLinkPreventClickTimer);
          this.slideImageLinkPreventClickTimer = window.setTimeout(() => {
            this.slideImageLinkPreventClick = false;
          }, 500);
        };
        const getPointerPoint = (event) => ({ x: event.clientX, y: event.clientY });
        const startDragTracking = (event) => {
          if (event.button !== undefined && event.button !== 0) return;

          const linkedSlide = event.target.closest('.slideshow__image-link, .slideshow__item-wrap--linked');
          if (!linkedSlide || !this.contains(linkedSlide)) return;

          const point = getPointerPoint(event);
          this.slideImageLinkDragState = {
            pointerId: event.pointerId,
            startX: point.x,
            startY: point.y,
            dragged: false
          };
          this.slideImageLinkPreventClick = false;
        };
        const trackDrag = (event) => {
          const dragState = this.slideImageLinkDragState;
          if (!dragState || (dragState.pointerId !== undefined && event.pointerId !== dragState.pointerId)) return;

          const point = getPointerPoint(event);
          const movedX = Math.abs(point.x - dragState.startX);
          const movedY = Math.abs(point.y - dragState.startY);

          if (movedX > dragThreshold || movedY > dragThreshold) {
            dragState.dragged = true;
            this.slideImageLinkPreventClick = true;
          }
        };
        const stopDragTracking = (event) => {
          const dragState = this.slideImageLinkDragState;
          if (!dragState || (dragState.pointerId !== undefined && event.pointerId !== dragState.pointerId)) return;

          if (dragState.dragged) {
            this.slideImageLinkPreventClick = true;
            resetPreventClick();
          }

          this.slideImageLinkDragState = null;
        };

        this.addEventListener('pointerdown', startDragTracking, { passive: true });
        this.addEventListener('pointermove', trackDrag, { passive: true });
        this.addEventListener('pointerup', stopDragTracking, { passive: true });
        this.addEventListener('pointercancel', stopDragTracking, { passive: true });

        this.addEventListener(
          'click',
          (event) => {
            const interactiveElement = event.target.closest(
              'a:not(.slideshow__image-link), button, input, select, textarea, summary, .swiper-controls, .swiper-pagination, .swiper-button, .btn'
            );
            if (interactiveElement && this.contains(interactiveElement)) return;

            const link = event.target.closest('.slideshow__image-link');
            const linkedSlide = event.target.closest('.slideshow__item-wrap--linked');
            if ((!link || !this.contains(link)) && (!linkedSlide || !this.contains(linkedSlide))) return;

            event.preventDefault();
            event.stopPropagation();

            const slideLink = link || linkedSlide.querySelector('.slideshow__image-link');
            const href = slideLink?.href || linkedSlide?.dataset.slideLink;
            if (!href) return;

            const swiper = this.sliderInstance?.slider;
            if (this.slideImageLinkPreventClick || swiper?.allowClick === false) {
              this.slideImageLinkPreventClick = false;
              window.clearTimeout(this.slideImageLinkPreventClickTimer);
              return;
            }

            if (slideLink?.target === '_blank') {
              window.open(href, '_blank', 'noopener');
              return;
            }

            window.location.href = href;
          },
          true
        );
      }

      setupZoom() {
        if (!this.enableZoom) return;

        this.destroyZoom();

        const pageWidthEl = this.querySelector('.page-width');
        const paddingLeft = window.getComputedStyle(pageWidthEl).getPropertyValue('padding-left');
        this.zoom = Math.min(parseFloat(paddingLeft.replace('px', '')), window.innerWidth * 0.04);

        if (this.zoom > 15 && FoxTheme.a11y.shouldAnimate()) {
          this.initZoom();
        }
      }

      initSlider() {
        const additionModules = [FoxTheme.Swiper.Autoplay, FoxTheme.Swiper.EffectFade];

        this.sliderOptions = {
          slidesPerView: 1,
          spaceBetween: 10,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          threshold: 2,
          effect: 'fade',
          on: {
            init: this.handleAfterInit.bind(this)
          },
          fadeEffect: {
            crossFade: true
          }
        };

        if (this.sliderControls) {
          this.sliderOptions.navigation = {
            nextEl: this.sliderControls.querySelector('.swiper-button-next'),
            prevEl: this.sliderControls.querySelector('.swiper-button-prev')
          };
          this.sliderOptions.pagination = {
            el: this.sliderControls.querySelector('.swiper-pagination'),
            clickable: false
          };
        }

        const autoplayDelay = parseInt(this.dataset.autoplay);
        if (autoplayDelay > 0) {
          this.sliderOptions = {
            ...this.sliderOptions,
            autoplay: {
              delay: autoplayDelay,
              disableOnInteraction: false
            }
          };
        }

        this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions, additionModules);
        this.sliderInstance.init();
        this.setupAutoplayHoverPause();
        this.setupInitialAutoplayDelay();
        this.setupPaginationClickHandlers();
        this.sliderInstance.slider.on('realIndexChange', this.handleSlideChange.bind(this));
        this.applyPendingPaginationSelection();

        if (this.sliderInstance) {
          this.selectedElement = this.sliderInstance.slider.slides[this.sliderInstance.slider.activeIndex];
          this.onReady(this.selectedElement, this.sliderInstance.slider.slides);

          // Fix accessibility
          const focusableElements = FoxTheme.a11y.getFocusableElements(this);
          focusableElements.forEach((element) => {
            if (!element.classList.contains('swiper-button') && !element.classList.contains('btn')) {
              element.addEventListener('focusin', () => {
                const slide = element.closest('.swiper-slide');
                this.sliderInstance.slider.slideToLoop(this.sliderInstance.slider.slides.indexOf(slide));
              });
            }
          });
        }
      }

      setupAutoplayHoverPause() {
        const swiper = this.sliderInstance?.slider;
        if (!swiper?.autoplay || !this.sliderOptions?.autoplay) return;

        this.pauseAutoplayOnHover = this.pauseAutoplayOnHover.bind(this);
        this.resumeAutoplayAfterHover = this.resumeAutoplayAfterHover.bind(this);

        this.addEventListener('pointerenter', this.pauseAutoplayOnHover);
        this.addEventListener('pointerleave', this.resumeAutoplayAfterHover);
        this.addEventListener('mouseenter', this.pauseAutoplayOnHover);
        this.addEventListener('mouseleave', this.resumeAutoplayAfterHover);
        this.addEventListener('focusin', this.pauseAutoplayOnHover);
        this.addEventListener('focusout', (event) => {
          if (!event.relatedTarget || !this.contains(event.relatedTarget)) {
            this.resumeAutoplayAfterHover();
          }
        });

        window.setTimeout(() => {
          this.autoplayHoverPauseReady = true;
        }, 1200);
      }

      setupInitialAutoplayDelay() {
        const configuredStartDelay = parseInt(this.dataset.autoplayStartDelay, 10);
        const startDelay = Number.isNaN(configuredStartDelay) ? 0 : Math.min(configuredStartDelay, 300);
        const swiper = this.sliderInstance?.slider;
        if (startDelay < 0 || !swiper?.autoplay || !this.sliderOptions?.autoplay) return;

        const scheduleStart = () => {
          if (!this.initialAutoplayDelayActive) return;
          if (this.autoplayStartOnInteraction) {
            this.waitForAutoplayInteraction();
            return;
          }

          window.clearTimeout(this.initialAutoplayDelayTimer);
          this.initialAutoplayDelayTimer = window.setTimeout(() => this.startInitialAutoplay(), startDelay);
        };

        this.initialAutoplayDelayActive = true;
        this.style.setProperty('--metamall-autoplay-progress-width', '0%');
        this.classList.add('slideshow-wrapper--autoplay-paused');

        if (typeof swiper.autoplay.stop === 'function') {
          swiper.autoplay.stop();
        }

        this.waitForInitialSlideImage(scheduleStart);
      }

      getInitialSlideImage() {
        const swiper = this.sliderInstance?.slider;
        const activeSlide =
          swiper?.slides?.[swiper.activeIndex] ||
          this.querySelector('.swiper-slide-active') ||
          this.querySelector('.swiper-slide[data-index="0"]');

        return activeSlide?.querySelector('img');
      }

      loadDeferredSlide(slide) {
        if (!slide) return;

        slide.querySelectorAll('source[data-slideshow-srcset]').forEach((source) => {
          if (!source.getAttribute('srcset') && source.dataset.slideshowSrcset) {
            source.setAttribute('srcset', source.dataset.slideshowSrcset);
          }
        });

        slide.querySelectorAll('img[data-slideshow-deferred-image]').forEach((image) => {
          if (image.dataset.slideshowImageLoaded === 'true') return;

          if (image.getAttribute('loading') === 'lazy') {
            image.setAttribute('loading', 'eager');
          }
          if (image.dataset.srcset) {
            image.setAttribute('srcset', image.dataset.srcset);
          }
          if (image.dataset.sizes) {
            image.setAttribute('sizes', image.dataset.sizes);
          }
          if (image.dataset.src) {
            image.setAttribute('src', image.dataset.src);
          }

          image.dataset.slideshowImageLoaded = 'true';
        });
      }

      getSlideCount() {
        const indexes = new Set();
        this.querySelectorAll('.swiper-slide[data-index]').forEach((slide) => {
          const index = parseInt(slide.dataset.index, 10);
          if (!Number.isNaN(index)) {
            indexes.add(index);
          }
        });
        const bulletCount = this.sliderControls?.querySelectorAll('.swiper-pagination-bullet').length || 0;
        return Math.max(indexes.size, bulletCount);
      }

      loadDeferredSlideByIndex(index) {
        const slideCount = this.getSlideCount();
        if (slideCount <= 0 || !Number.isFinite(index)) return;

        const normalizedIndex = ((index % slideCount) + slideCount) % slideCount;
        this.querySelectorAll(
          `.swiper-slide[data-index="${normalizedIndex}"], .swiper-slide[data-swiper-slide-index="${normalizedIndex}"]`
        ).forEach((slide) => {
          this.loadDeferredSlide(slide);
        });
      }

      scheduleDeferredSlidePreload() {
        if (this.deferredSlidePreloadScheduled) return;
        this.deferredSlidePreloadScheduled = true;

        const preloadNextSlide = () => {
          window.setTimeout(() => {
            this.loadDeferredSlideByIndex(this.selectedIndex + 1);
          }, 1800);
        };

        if (document.readyState === 'complete') {
          preloadNextSlide();
        } else {
          window.addEventListener('load', preloadNextSlide, { once: true });
        }
      }

      waitForInitialSlideImage(callback) {
        const image = this.getInitialSlideImage();
        if (!image || image.complete || image.naturalWidth > 0) {
          callback();
          return;
        }

        let finished = false;
        const finish = () => {
          if (finished) return;
          finished = true;
          image.removeEventListener('load', finish);
          image.removeEventListener('error', finish);
          window.clearTimeout(this.initialAutoplayMediaWaitTimer);
          this.initialAutoplayMediaWaitTimer = null;
          callback();
        };

        image.addEventListener('load', finish, { once: true });
        image.addEventListener('error', finish, { once: true });
        this.initialAutoplayMediaWaitTimer = window.setTimeout(finish, 1200);
      }

      clearInitialAutoplayDelay() {
        window.clearTimeout(this.initialAutoplayDelayTimer);
        window.clearTimeout(this.initialAutoplayMediaWaitTimer);
        if (this.initialAutoplayLoadHandler) {
          window.removeEventListener('load', this.initialAutoplayLoadHandler);
        }

        this.initialAutoplayDelayActive = false;
        this.initialAutoplayDelayTimer = null;
        this.initialAutoplayLoadHandler = null;
        this.initialAutoplayMediaWaitTimer = null;
        this.clearAutoplayInteractionListeners();
      }

      waitForAutoplayInteraction() {
        this.clearAutoplayInteractionListeners();
        this.autoplayInteractionHandler = () => this.startInitialAutoplay();

        this.autoplayInteractionEvents.forEach((eventName) => {
          window.addEventListener(eventName, this.autoplayInteractionHandler, { once: true, passive: true });
        });
      }

      clearAutoplayInteractionListeners() {
        if (!this.autoplayInteractionHandler) return;

        this.autoplayInteractionEvents.forEach((eventName) => {
          window.removeEventListener(eventName, this.autoplayInteractionHandler);
        });
        this.autoplayInteractionHandler = null;
      }

      startInitialAutoplay() {
        const swiper = this.sliderInstance?.slider;
        this.clearInitialAutoplayDelay();
        this.releaseAutoplayProgress();
        this.scheduleDeferredSlidePreload();

        if (!this.isConnected || !swiper?.autoplay) return;
        if (typeof swiper.autoplay.start === 'function') {
          swiper.autoplay.start();
        }
      }

      setupPaginationClickHandlers() {
        const pagination = this.sliderControls?.querySelector('.swiper-pagination');
        if (!pagination || !this.sliderInstance?.slider) return;

        this.handlePaginationPointerDown = this.handlePaginationPointerDown.bind(this);
        this.handlePaginationClick = this.handlePaginationClick.bind(this);

        pagination.addEventListener('pointerdown', this.handlePaginationPointerDown, true);
        pagination.addEventListener('click', this.handlePaginationClick, true);
      }

      clearPaginationPlaceholder() {
        const pagination = this.sliderControls?.querySelector('.swiper-pagination');
        if (!pagination) return;

        const placeholderBullets = Array.from(
          pagination.querySelectorAll('.metamall-slideshow-pagination-placeholder-bullet')
        );
        const realBullets = Array.from(
          pagination.querySelectorAll('.swiper-pagination-bullet:not(.metamall-slideshow-pagination-placeholder-bullet)')
        );

        pagination.classList.remove('metamall-slideshow-pagination-placeholder');
        pagination.removeAttribute('data-metamall-pagination-placeholder');

        if (realBullets.length > 0) {
          placeholderBullets.forEach((bullet) => bullet.remove());
          return;
        }

        placeholderBullets.forEach((bullet) => {
          bullet.classList.remove('metamall-slideshow-pagination-placeholder-bullet');
          bullet.removeAttribute('aria-hidden');
        });
      }

      getPendingPaginationIndex() {
        const pendingIndex = parseInt(this.dataset.metamallPendingPaginationIndex, 10);
        if (!Number.isNaN(pendingIndex)) return pendingIndex;

        const sectionPending = window.__metamallSlideshowPendingPagination?.[this.sectionId];
        if (Number.isInteger(sectionPending)) return sectionPending;

        return -1;
      }

      clearPendingPaginationIndex() {
        delete this.dataset.metamallPendingPaginationIndex;
        if (window.__metamallSlideshowPendingPagination && this.sectionId) {
          delete window.__metamallSlideshowPendingPagination[this.sectionId];
        }
      }

      applyPendingPaginationSelection() {
        const index = this.getPendingPaginationIndex();
        if (index < 0) return;

        this.clearPendingPaginationIndex();
        this.navigateToPaginationIndex(index);
      }

      getPaginationBulletIndex(event) {
        const pagination = this.sliderControls?.querySelector('.swiper-pagination');
        const bullet = event.target.closest('.swiper-pagination-bullet');
        if (!pagination || !bullet || !pagination.contains(bullet)) return -1;

        return Array.from(pagination.querySelectorAll('.swiper-pagination-bullet')).indexOf(bullet);
      }

      handlePaginationPointerDown(event) {
        const index = this.getPaginationBulletIndex(event);
        if (index < 0) return;

        this.paginationPointerIndex = index;
      }

      handlePaginationClick(event) {
        const pointerIndex = Number.isInteger(this.paginationPointerIndex) ? this.paginationPointerIndex : -1;
        const index = pointerIndex >= 0 ? pointerIndex : this.getPaginationBulletIndex(event);
        this.paginationPointerIndex = null;

        if (index < 0) return;

        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }

        this.navigateToPaginationIndex(index);
      }

      navigateToPaginationIndex(index) {
        const swiper = this.sliderInstance?.slider;
        const bullets = this.sliderControls?.querySelectorAll('.swiper-pagination-bullet') || [];
        if (!swiper || bullets.length === 0) return;

        const targetIndex = Math.min(Math.max(index, 0), bullets.length - 1);
        this.loadDeferredSlideByIndex(targetIndex);
        if (typeof swiper.slideToLoop === 'function') {
          swiper.slideToLoop(targetIndex);
        } else if (typeof swiper.slideTo === 'function') {
          swiper.slideTo(targetIndex);
        }

        this.resetPaginationBulletWidths(targetIndex);
        this.restartAutoplayAfterManualNavigation();
      }

      resetPaginationBulletWidths(activeIndex) {
        const bullets = this.sliderControls?.querySelectorAll('.swiper-pagination-bullet') || [];
        bullets.forEach((bullet, index) => {
          if (index === activeIndex) {
            bullet.style.removeProperty('width');
            bullet.style.removeProperty('transition-property');
          } else {
            bullet.style.width = 'var(--swiper-pagination-bullet-width, var(--swiper-pagination-bullet-size, 8px))';
            bullet.style.transitionProperty = 'background-color, opacity';
          }
        });
      }

      restartAutoplayAfterManualNavigation() {
        const swiper = this.sliderInstance?.slider;
        if (!swiper?.autoplay || !this.sliderOptions?.autoplay) return;

        if (this.initialAutoplayDelayActive) {
          this.clearInitialAutoplayDelay();
        }
        this.releaseAutoplayProgress();

        if (typeof swiper.autoplay.stop === 'function') {
          swiper.autoplay.stop();
        }
        if (typeof swiper.autoplay.start === 'function') {
          swiper.autoplay.start();
        }
      }

      getActivePaginationBullet() {
        return this.sliderControls?.querySelector('.swiper-pagination-bullet-active');
      }

      freezeAutoplayProgress() {
        const activeBullet = this.getActivePaginationBullet();
        if (!activeBullet) return;

        const progressWidth = window.getComputedStyle(activeBullet, '::after').getPropertyValue('width');
        if (progressWidth) {
          this.style.setProperty('--metamall-autoplay-progress-width', progressWidth);
        }
        this.classList.add('slideshow-wrapper--autoplay-paused');
      }

      releaseAutoplayProgress() {
        this.classList.remove('slideshow-wrapper--autoplay-paused');
        this.style.removeProperty('--metamall-autoplay-progress-width');
      }

      pauseAutoplayOnHover(event) {
        if ((event?.type === 'pointerenter' || event?.type === 'mouseenter') && !this.autoplayHoverPauseReady) return;

        const swiper = this.sliderInstance?.slider;
        if (!swiper?.autoplay || !this.sliderOptions?.autoplay) return;

        this.autoplayPausedByHover = true;
        this.freezeAutoplayProgress();

        if (typeof swiper.autoplay.pause === 'function' && typeof swiper.autoplay.resume === 'function') {
          this.autoplayPauseMode = 'pause';
          swiper.autoplay.pause();
        } else if (typeof swiper.autoplay.stop === 'function') {
          this.autoplayPauseMode = 'stop';
          swiper.autoplay.stop();
        }
      }

      resumeAutoplayAfterHover() {
        if (!this.autoplayPausedByHover) return;

        const swiper = this.sliderInstance?.slider;
        this.autoplayPausedByHover = false;
        if (!swiper?.autoplay || !this.sliderOptions?.autoplay) return;
        if (this.initialAutoplayDelayActive) return;

        this.releaseAutoplayProgress();

        if (this.autoplayPauseMode === 'pause' && typeof swiper.autoplay.resume === 'function') {
          swiper.autoplay.resume();
        } else {
          if (typeof swiper.autoplay.stop === 'function') {
            swiper.autoplay.stop();
          }
          if (typeof swiper.autoplay.start === 'function') {
            swiper.autoplay.start();
          }
        }
        this.autoplayPauseMode = null;
      }

      onReady(selectedElement) {
        this.loadDeferredSlide(selectedElement);

        if (selectedElement.dataset.type === 'video') {
          const videoElement = FoxTheme.utils.displayedMedia(selectedElement.querySelectorAll('video-element'));
          videoElement?.play();
        }

        if (FoxTheme.a11y.shouldAnimate()) {
          const motionEls = selectedElement.querySelectorAll('motion-element');
          motionEls.forEach((motionEl) => {
            setTimeout(() => {
              motionEl && motionEl.refreshAnimation();
            });
          });
        }
      }

      handleAfterInit() {
        this.removeAttribute('data-media-loading');
        this.clearPaginationPlaceholder();
        this.scheduleDeferredSlidePreload();

        // Fix active bullet not transition on the first time.
        if (this.sliderControls) {
          const activeBullet = this.sliderControls.querySelector('.swiper-pagination-bullet-active');

          if (activeBullet) {
            activeBullet.classList.remove('swiper-pagination-bullet-active');
            activeBullet.offsetHeight; // Trigger reflow.
            activeBullet.classList.add('swiper-pagination-bullet-active');
          }
        }
      }

      handleSlideChange(swiper) {
        const { slides, realIndex, activeIndex } = swiper;
        this.selectedIndex = realIndex;
        this.resetPaginationBulletWidths(realIndex);
        this.loadDeferredSlide(slides[activeIndex]);
        window.setTimeout(() => {
          this.loadDeferredSlideByIndex(realIndex + 1);
        }, 400);

        this.updateControlsScheme(slides[activeIndex]);
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
          const fromElements = this.querySelectorAll(`[data-swiper-slide-index="${oldValue}"]`);
          const toElements = this.querySelectorAll(`[data-swiper-slide-index="${newValue}"]`);

          fromElements.forEach((fromElement) => {
            if (fromElement.dataset.type === 'video') {
              const videoElement = FoxTheme.utils.displayedMedia([fromElement.querySelector('video-element')]);
              videoElement && videoElement.pause();
            }

            const motionEls = fromElement.querySelectorAll('motion-element');
            if (FoxTheme.a11y.shouldAnimate()) {
              motionEls.forEach((motionEl) => {
                if (motionEl.hasAttribute('data-text')) {
                  motionEl.resetAnimation();
                }
              });
            }
          });

          toElements.forEach((toElement) => {
            setTimeout(() => {
              if (toElement.classList.contains('swiper-slide-active')) {
                if (toElement.dataset.type === 'video') {
                  const videoElement = FoxTheme.utils.displayedMedia([toElement.querySelector('video-element')]);
                  videoElement && videoElement.play();
                }

                const motionEls = toElement.querySelectorAll('motion-element');
                if (FoxTheme.a11y.shouldAnimate()) {
                  motionEls.forEach((motionEl) => {
                    setTimeout(() => {
                      motionEl && motionEl.refreshAnimation();
                    });
                  });
                }
              }
            });
          });
        }
      }

      updateControlsScheme(activeSlide) {
        if (this.sliderControls) {
          const classesToRemove = Array.from(this.sliderControls.classList).filter((className) =>
            className.startsWith('color-')
          );
          classesToRemove.forEach((className) => this.sliderControls.classList.remove(className));
          const colorScheme = activeSlide.dataset.colorScheme;
          this.sliderControls.classList.add(colorScheme);
        }
      }

      initZoom() {
        if (this.containerWidth !== 'full') return;

        const motionTarget = {
          target: this.section,
          offset: [
            [-0.1, 0],
            [0.7, 0]
          ]
        };

        this.querySelectorAll('.slideshow__item-wrap').forEach((slide) => {
          const slideBg = slide.querySelector('.slideshow__bg');

          this.cleanupScrollAnimations.push(
            FoxTheme.Motion.scroll(
              FoxTheme.Motion.animate(slideBg, {
                transform: [`scale(1) translateZ(0)`, `scale(1.1) translateZ(0)`]
              }),
              motionTarget
            )
          );
        });

        this.cleanupScrollAnimations.push(
          FoxTheme.Motion.scroll(
            FoxTheme.Motion.animate(this, {
              clipPath: [`inset(0%)`, `inset(${Math.min(50, this.zoom)}px ${this.zoom}px round ${this.borderRadius}px)`]
            }),
            motionTarget
          )
        );

        if (this.sliderControls) {
          this.cleanupScrollAnimations.push(
            FoxTheme.Motion.scroll(
              FoxTheme.Motion.animate(this.sliderControls, {
                bottom: [`0`, `${Math.min(50, this.zoom)}px`]
              }),
              motionTarget
            )
          );
        }
      }

      destroyZoom() {
        this.cleanupScrollAnimations.forEach((fn) => fn());
        this.cleanupScrollAnimations = [];
        this.style.clipPath = 'none';
        if (this.sliderControls) this.sliderControls.style.bottom = 0;
      }
    }
  );
}

